import {
  CloseOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
  SaveOutlined,
  TableOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Table, message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import PurchaseHeaderForm, { financialYearCode } from "./PurchaseHeaderForm";
import axios from "axios";
import { CREATE_jwel, CREATE_jwel1 } from "../../Config/Config";

/* Full 4-char financial year string e.g. "2627" for Apr-2026 → Mar-2027 */
const financialYearFull = (d = new Date()) => {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(y).slice(-2)}${String(y + 1).slice(-2)}`;
};

const C = {
  pri: "#4f46e5",
  priDark: "#3730a3",
  priLight: "#eef2ff",
  priMid: "#c7d2fe",

  gold: "#d97706",
  goldLight: "#fef3c7",
  goldMid: "#fde68a",

  ok: "#059669",
  okLight: "#d1fae5",

  danger: "#e11d48",
  dangerLight: "#ffe4e6",

  bg: "#f8f7ff",
  surface: "#ffffff",
  border: "#e5e7eb",
  borderHov: "#a5b4fc",
  textPri: "#1e1b4b",
  textSec: "#6366f1",
  textMuted: "#9ca3af",
  row1: "#fdfcff",
  row2: "#f5f3ff",
  rowHov: "#ede9fe",
};
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  .eup-wrap * { box-sizing: border-box; }
  .eup-wrap {
    font-family: 'Inter', sans-serif;
    background: ${C.bg};
    /* Lock to available viewport — no page scroll, only table body scrolls */
    height: calc(100vh - 60px);
    max-height: calc(100vh - 110px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-width: 100%;
  }

  /* ── page header ── */
  .eup-header {
    background: linear-gradient(135deg, #312e81 0%, #4f46e5 55%, #7c3aed 100%);
    padding: 16px 22px;
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
    position: relative; overflow: hidden;
    border-radius: 10px 10px 0 0;
    flex-shrink: 0;
  }
  .eup-header::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 65%);
    pointer-events: none;
  }
  .eup-header-shimmer {
    position: absolute; top: 0; left: -70%; width: 40%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    transform: skewX(-18deg);
    animation: eupShimmer 5s ease-in-out infinite 0.5s;
    pointer-events: none;
  }
  @keyframes eupShimmer { 0%{ left:-70% } 100%{ left:140% } }

  .eup-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: 0.2px; }
  .eup-header-sub   { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 2px; }

  .eup-chip {
    display: flex; flex-direction: column; align-items: center;
    padding: 5px 16px;
    background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
    border-radius: 10px; min-width: 72px; cursor: default;
    transition: background 0.2s, transform 0.2s;
    backdrop-filter: blur(6px);
  }
  .eup-chip:hover { background: rgba(255,255,255,0.22); transform: translateY(-2px); }
  .eup-chip-val  { font-size: 15px; font-weight: 800; color: #fff; line-height: 1.2; }
  .eup-chip-lbl  { font-size: 9px; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: 0.7px; margin-top: 2px; }

  /* ── toolbar ── */
  .eup-toolbar {
    background: ${C.surface};
    border-bottom: 1px solid ${C.border};
    padding: 12px 20px;
    display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    flex-shrink: 0;
  }

  /* ── file pill ── */
  .eup-file-pill {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(90deg, #ecfdf5, #d1fae5);
    border: 1.5px solid #6ee7b7;
    border-radius: 20px; padding: 4px 14px 4px 10px;
    font-size: 13px; font-weight: 600; color: #065f46;
    animation: eupPop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  @keyframes eupPop { from{ opacity:0; transform:scale(0.82) } to{ opacity:1; transform:scale(1) } }

  /* ── icon pill buttons (toolbar) ── */
  .eup-btn {
    display: inline-flex; align-items: center; gap: 7px;
    border-radius: 9px; padding: 7px 16px;
    font-size: 13px; font-weight: 600; cursor: pointer;
    border: 1.5px solid transparent;
    transition: transform 0.15s, box-shadow 0.15s, background 0.15s, border-color 0.15s;
    outline: none; line-height: 1;
  }
  .eup-btn:hover { transform: translateY(-1px); }
  .eup-btn:active { transform: translateY(0); }

  .eup-btn-upload {
    background: ${C.priLight}; color: ${C.pri};
    border-color: ${C.priMid};
  }
  .eup-btn-upload:hover {
    background: ${C.priMid}; border-color: ${C.pri};
    box-shadow: 0 4px 12px rgba(79,70,229,0.2);
  }

  .eup-btn-clear {
    background: ${C.dangerLight}; color: ${C.danger};
    border-color: #fda4af;
  }
  .eup-btn-clear:hover {
    background: #fecdd3; border-color: ${C.danger};
    box-shadow: 0 4px 12px rgba(225,29,72,0.18);
  }

  .eup-btn-save {
    background: linear-gradient(135deg, #312e81, ${C.pri});
    color: #fff; border-color: transparent;
    box-shadow: 0 3px 12px rgba(79,70,229,0.35);
  }
  .eup-btn-save:hover {
    background: linear-gradient(135deg, ${C.priDark}, #6366f1);
    box-shadow: 0 6px 20px rgba(79,70,229,0.45);
  }

  /* ── drop zone ── */
  .eup-dropzone {
    margin: 32px auto;
    max-width: 540px;
    border: 2px dashed ${C.border};
    border-radius: 20px;
    background: ${C.surface};
    padding: 56px 36px;
    text-align: center;
    cursor: pointer;
    transition: all 0.22s ease;
    box-shadow: 0 4px 24px rgba(79,70,229,0.06);
  }
  .eup-dropzone:hover, .eup-dropzone.dragging {
    border-color: ${C.pri};
    background: ${C.priLight};
    box-shadow: 0 0 0 5px rgba(79,70,229,0.08), 0 8px 32px rgba(79,70,229,0.12);
    transform: translateY(-2px);
  }

  .eup-dz-icon {
    width: 80px; height: 80px; border-radius: 50%;
    background: ${C.priLight}; border: 2px solid ${C.priMid};
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 22px; font-size: 34px; color: ${C.pri};
    transition: background 0.2s, transform 0.2s;
  }
  .eup-dropzone:hover .eup-dz-icon,
  .eup-dropzone.dragging .eup-dz-icon {
    background: ${C.pri}; color: #fff; transform: scale(1.08);
    border-color: ${C.pri};
  }
  .eup-dz-title {
    font-size: 18px; font-weight: 700; color: ${C.textPri}; margin-bottom: 8px;
    transition: color 0.2s;
  }
  .eup-dropzone:hover .eup-dz-title { color: ${C.pri}; }

  .eup-dz-sub { font-size: 13px; color: ${C.textMuted}; margin-bottom: 28px; line-height: 1.6; }
  .eup-dz-ext { font-weight: 700; color: ${C.ok}; }

  .eup-dz-btn {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, #312e81, ${C.pri});
    color: #fff; border: none; border-radius: 12px;
    padding: 11px 30px; font-size: 14px; font-weight: 700; cursor: pointer;
    box-shadow: 0 4px 16px rgba(79,70,229,0.35);
    transition: transform 0.15s, box-shadow 0.15s;
    letter-spacing: 0.2px;
  }
  .eup-dz-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(79,70,229,0.45); }
  .eup-dz-btn:active { transform: translateY(0); }

  /* ── loading spinner ── */
  @keyframes eupSpin { to{ transform: rotate(360deg) } }
  .eup-spinner {
    width: 48px; height: 48px; border-radius: 50%;
    border: 4px solid ${C.priMid};
    border-top-color: ${C.pri};
    animation: eupSpin 0.75s linear infinite;
  }

  /* ── table card ── */
  .eup-card {
    background: ${C.surface};
    border-radius: 0 0 10px 10px;
    box-shadow: 0 4px 24px rgba(79,70,229,0.08);
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;        /* lets flex children shrink below content size */
    max-width: 100%;
    min-width: 0;
    animation: eupSlideUp 0.3s cubic-bezier(0.22,1,0.36,1) both;
  }
  @keyframes eupSlideUp { from{ opacity:0; transform:translateY(10px) } to{ opacity:1; transform:translateY(0) } }

  /* sub-header */
  .eup-subhdr {
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;
    padding: 9px 18px;
    background: linear-gradient(90deg, #f5f3ff, #faf5ff);
    border-bottom: 1px solid #ede9fe;
    flex-shrink: 0;
  }
  .eup-subhdr-text { font-size: 13px; color: ${C.textMuted}; }
  .eup-subhdr-text strong { color: ${C.textPri}; }

  .eup-editing-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: ${C.goldLight}; border: 1.5px solid ${C.goldMid};
    color: #92400e; font-size: 12px; font-weight: 700;
    padding: 2px 12px; border-radius: 20px;
    animation: eupPop 0.2s ease both;
  }

  /* table wrap — fills the remaining card height; its height is measured
     in JS and handed to Ant Design as scroll.y */
  .eup-table-wrap {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    width: 100%;
  }

  /* ── Ant table overrides ── */
  .eup-table .ant-table-thead > tr > th {
    color: #fff !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.6px !important;
    text-transform: uppercase !important;
    border-bottom: none !important;
    padding: 12px 12px !important;
    white-space: nowrap !important;
  }
  .eup-table .ant-table-thead > tr > th::before {
    background-color: rgba(255,255,255,0.18) !important;
    width: 1px !important;
  }
  .eup-table .ant-table-thead > tr {
    background: none !important;
  }
  .eup-table .ant-table-tbody > tr > td {
    padding: 8px 12px !important;
    font-size: 13px !important;
    color: ${C.textPri} !important;
    border-bottom: 1px solid #ede9fe !important;
    transition: background 0.15s !important;
  }
  .eup-table .ant-table-tbody > tr:nth-child(odd) > td  { background: ${C.row1} !important; }
  .eup-table .ant-table-tbody > tr:nth-child(even) > td { background: ${C.row2} !important; }
  .eup-table .ant-table-tbody > tr:hover > td           { background: ${C.rowHov} !important; }

  /* sticky col overrides */
  .eup-table .ant-table-cell-fix-left  { z-index: 3 !important; }
  .eup-table .ant-table-cell-fix-right { z-index: 3 !important; }

  .eup-table .ant-table-tbody > tr > td.eup-sno-td {
    background: #eef2ff !important;
  }
  .eup-table .ant-table-tbody > tr > td.eup-act-td {
    background: #faf5ff !important;
  }
  .eup-table .ant-table-tbody > tr:hover > td.eup-sno-td { background: #e0e7ff !important; }
  .eup-table .ant-table-tbody > tr:hover > td.eup-act-td { background: #f3e8ff !important; }

  /* columns must not grow beyond their declared width */
  .eup-table .ant-table-cell { word-break: break-word !important; }
  .eup-table .ant-table-thead > tr > th,
  .eup-table .ant-table-tbody > tr > td {
    max-width: 0;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  .eup-table .ant-table { table-layout: fixed !important; }

  /* ── row action buttons ── */
  .eup-act-btn {
    width: 32px; height: 32px; border-radius: 9px;
    border: 1.5px solid transparent;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 14px; cursor: pointer; outline: none;
    transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
  }
  .eup-act-btn:hover { transform: translateY(-2px); }
  .eup-act-btn:active { transform: translateY(0); }
  .eup-act-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none !important; }

  .eup-act-edit  { background: ${C.priLight};  color: ${C.pri};    border-color: ${C.priMid};  }
  .eup-act-edit:not(:disabled):hover  { background: ${C.pri};    color: #fff; box-shadow: 0 4px 12px rgba(79,70,229,0.35); }

  .eup-act-del   { background: ${C.dangerLight}; color: ${C.danger}; border-color: #fda4af; }
  .eup-act-del:not(:disabled):hover   { background: ${C.danger};  color: #fff; box-shadow: 0 4px 12px rgba(225,29,72,0.3); }

  .eup-act-save  { background: ${C.okLight};   color: ${C.ok};    border-color: #6ee7b7;  }
  .eup-act-save:not(:disabled):hover  { background: ${C.ok};     color: #fff; box-shadow: 0 4px 12px rgba(5,150,105,0.3); }

  .eup-act-cancel{ background: #f3f4f6; color: #6b7280; border-color: #d1d5db; }
  .eup-act-cancel:not(:disabled):hover{ background: #e5e7eb; color: #374151; }

  /* ── editable input cell ── */
  .eup-cell-input.ant-input {
    border-radius: 7px !important;
    font-size: 12px !important;
    border-color: ${C.priMid} !important;
    box-shadow: 0 0 0 2px rgba(79,70,229,0.12) !important;
    transition: box-shadow 0.15s !important;
  }
  .eup-cell-input.ant-input:focus {
    box-shadow: 0 0 0 3px rgba(79,70,229,0.22) !important;
    border-color: ${C.pri} !important;
  }

  /* ── footer save bar ── */
  .eup-footer {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 12px;
    padding: 14px 22px;
    background: linear-gradient(90deg, #f5f3ff, #faf5ff);
    border-top: 1px solid #ede9fe;
    border-radius: 0 0 10px 10px;
    flex-shrink: 0;
  }
  .eup-footer-info {
    display: flex; align-items: center; gap: 10px; font-size: 13px; color: ${C.textMuted};
  }
  .eup-footer-info strong { color: ${C.textPri}; font-weight: 700; }

  .eup-save-final {
    display: inline-flex; align-items: center; gap: 9px;
    background: linear-gradient(135deg, #312e81, ${C.pri}, #7c3aed);
    background-size: 200% 200%; background-position: 0% 50%;
    color: #fff; border: none; border-radius: 12px;
    padding: 11px 28px; font-size: 14px; font-weight: 700; cursor: pointer;
    box-shadow: 0 4px 18px rgba(79,70,229,0.4);
    transition: transform 0.15s, box-shadow 0.15s, background-position 0.4s;
    letter-spacing: 0.2px;
  }
  .eup-save-final:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(79,70,229,0.5);
    background-position: 100% 50%;
  }
  .eup-save-final:active { transform: translateY(0); }
  .eup-save-final:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  /* ── delete modal ── */
  .eup-modal-del-icon {
    width: 56px; height: 56px; border-radius: 16px;
    background: ${C.dangerLight}; color: ${C.danger};
    display: flex; align-items: center; justify-content: center;
    font-size: 26px; margin: 0 auto 16px;
    animation: eupPop 0.25s ease both;
  }
    .eup-btn:focus-visible {
  border-color: ${C.pri};
  box-shadow: 0 0 0 3px rgba(79,70,229,0.35);
}
`;

const STOCK_SEQ_FIELD = "MAXNUM";
const HEADER_H = 44;
async function parseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  // works for both .xls (BIFF) and .xlsx
  const wb = XLSX.read(buffer, { type: "array", cellDates: false });

  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error("No worksheet found in the file.");

  // raw:false -> values come back as formatted text (dates, decimals as shown in Excel)
  const rows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    raw: false,
    defval: "",
    blankrows: false,
  });

  if (rows.length === 0) throw new Error("The worksheet is empty.");

  // Keep the original column index for each non-empty header so that data
  // stays aligned even when a header cell is blank. Duplicate names get a suffix.
  const seen = {};
  const cols = [];
  rows[0].forEach((h, idx) => {
    let name = String(h ?? "").trim();
    if (!name) return;
    if (seen[name] !== undefined) {
      seen[name] += 1;
      name = `${name} (${seen[name]})`;
    } else {
      seen[name] = 1;
    }
    cols.push({ name, idx });
  });

  const headers = cols.map((c) => c.name);
  const dataRows = rows
    .slice(1)
    .filter((r) => r.some((c) => String(c ?? "").trim() !== ""))
    .map((r) => cols.map((c) => String(r[c.idx] ?? "").trim()));

  return { headers, dataRows };
}
const DropZone = ({ onFile, dragging, onDragOver, onDrop, onDragLeave }) => (
  <div
    className={`eup-dropzone${dragging ? " dragging" : ""}`}
    onDragOver={onDragOver}
    onDrop={onDrop}
    onDragLeave={onDragLeave}
    onClick={onFile}
  >
    <div className="eup-dz-icon">
      {dragging ? <CloudUploadOutlined /> : <FileExcelOutlined />}
    </div>

    <div className="eup-dz-title">
      {dragging ? "Release to load your file" : "Upload an Excel File"}
    </div>

    <p className="eup-dz-sub">
      Drag &amp; drop anywhere on this card, or click to browse
      <br />
      <span className="eup-dz-ext">.xlsx</span>
      &nbsp;&nbsp;and&nbsp;&nbsp;
      <span className="eup-dz-ext">.xls</span>
      &nbsp; formats supported
    </p>

    <button className="eup-dz-btn">
      <UploadOutlined />
      Choose File
    </button>
  </div>
);

const SnoBadge = ({ n }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: "50%",
      background: `linear-gradient(135deg, #312e81, ${C.pri})`,
      color: "#fff",
      fontWeight: 800,
      fontSize: 11,
      letterSpacing: 0.2,
      boxShadow: "0 2px 6px rgba(79,70,229,0.35)",
    }}
  >
    {n}
  </span>
);

const Loader = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 18,
      padding: "72px 20px",
    }}
  >
    <div style={{ position: "relative", width: 56, height: 56 }}>
      <div className="eup-spinner" style={{ position: "absolute", inset: 0 }} />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          color: C.pri,
        }}
      >
        <FileExcelOutlined />
      </div>
    </div>
    <span
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: C.textMuted,
        letterSpacing: 0.3,
      }}
    >
      Reading Excel file…
    </span>
  </div>
);

const HDR_STOPS = [
  { p: 0, r: 0x1e, g: 0x13, b: 0x54 },
  { p: 0.3, r: 0x31, g: 0x2e, b: 0x81 },
  { p: 0.65, r: 0x4f, g: 0x46, b: 0xe5 },
  { p: 1, r: 0x7c, g: 0x3a, b: 0xed },
];

function hdrColor(t) {
  const clamped = Math.max(0, Math.min(1, t));
  let lo = HDR_STOPS[0],
    hi = HDR_STOPS[HDR_STOPS.length - 1];
  for (let i = 0; i < HDR_STOPS.length - 1; i++) {
    if (clamped >= HDR_STOPS[i].p && clamped <= HDR_STOPS[i + 1].p) {
      lo = HDR_STOPS[i];
      hi = HDR_STOPS[i + 1];
      break;
    }
  }
  const range = hi.p - lo.p || 1;
  const f = (clamped - lo.p) / range;
  const r = Math.round(lo.r + f * (hi.r - lo.r));
  const g = Math.round(lo.g + f * (hi.g - lo.g));
  const b = Math.round(lo.b + f * (hi.b - lo.b));
  return `rgb(${r},${g},${b})`;
}

const ExcelUploadPurchase = () => {
  const fileInputRef = useRef(null);
  const tableWrapRef = useRef(null);
  const [tableBodyH, setTableBodyH] = useState(400);
  const [headers, setHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const [messageApi, ctx] = message.useMessage();
  const [designBase, setDesignBase] = useState({ prefix: null, maxSeq: null });
  const uploadBtnRef = useRef(null);

  // ── purchase header (all form fields) ──
  const [header, setHeader] = useState({
    entryNo: "",
    date: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
    purchaseType: null,
    city: "",
    supplierObj: null,
    shortName: "",
    address: "",
    gstin: "",
    panNo: "",
    station: "LOCAL",
    invoiceNo: "",
    invoiceDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
    stockRecDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
  });

  const loadDesignBase = useCallback(async () => {
    const tenantName = localStorage.getItem("tenantName");
    const get = (tableName) =>
      axios.get(`${CREATE_jwel1}/api/Master/GetDataFromGivenTableName`, {
        params: { tableName },
        headers: { tenantName },
      });
    try {
      const [cfg, stock] = await Promise.all([
        get("counter_config"),
        get("current_stock"),
      ]);
      const p = Number(cfg.data?.[0]?.BARCODE_PREFIX);
      const rows = Array.isArray(stock.data) ? stock.data : [];
      if (rows.length && !(STOCK_SEQ_FIELD in rows[0])) {
        messageApi.warning(
          `Field "${STOCK_SEQ_FIELD}" not found in current_stock.`,
        );
      }
      const maxSeq = rows.reduce((m, r) => {
        const n = parseInt(r?.[STOCK_SEQ_FIELD], 10);
        return Number.isFinite(n) && n > m ? n : m;
      }, 0);
      setDesignBase({
        prefix: Number.isFinite(p) ? String(Math.trunc(p)) : "",
        maxSeq,
      });
    } catch {
      messageApi.error("Could not load design code settings.");
    }
  }, [messageApi]);

  useEffect(() => {
    loadDesignBase();
  }, [loadDesignBase]);

  // rows with their design code; recalculates when the purchase type changes
  const pcode = header.purchaseType?.PCode;
  const dataSource = useMemo(() => {
    const fy = financialYearCode();
    return tableData.map((r, i) => ({
      ...r,
      _dcode:
        pcode && designBase.prefix !== null && designBase.maxSeq !== null
          ? `${pcode}${fy}${designBase.prefix}${designBase.maxSeq + 1 + i}`
          : "",
    }));
  }, [tableData, pcode, designBase]);
  // Must be declared BEFORE the effect below that depends on it.
  const hasData = tableData.length > 0;

  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setTableBodyH(Math.floor(entry.contentRect.height));
    });
    ro.observe(el);
    setTableBodyH(Math.floor(el.getBoundingClientRect().height));

    return () => ro.disconnect();
  }, [hasData]);

  const processFile = useCallback(
    async (file) => {
      if (!file) return;
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["xlsx", "xls"].includes(ext)) {
        messageApi.error("Please upload a valid .xlsx or .xls file.");
        return;
      }
      setLoading(true);
      try {
        const { headers: hdrs, dataRows } = await parseExcelFile(file);
        setHeaders(hdrs);
        setFileName(file.name);
        const rows = dataRows.map((r, i) => {
          const obj = { key: i + 1, _sno: i + 1 };
          hdrs.forEach((h, ci) => {
            obj[h] = r[ci] ?? "";
          });
          return obj;
        });
        setTableData(rows);
        loadDesignBase();
        messageApi.success(
          `Loaded ${rows.length} record(s) from "${file.name}"`,
        );
      } catch (err) {
        messageApi.error(`Failed to read file: ${err.message}`);
      } finally {
        setLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [messageApi, loadDesignBase],
  );

  const onInputChange = (e) => processFile(e.target.files?.[0]);
  const onDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  /* ── save all ── */
  const handleSaveAll = async () => {
    if (!tableData.length) {
      messageApi.warning("No data to save.");
      return;
    }
    if (!header.purchaseType) {
      messageApi.warning("Please select a Purchase Type.");
      return;
    }
    if (!header.supplierObj) {
      messageApi.warning("Please select a Supplier.");
      return;
    }
    if (!header.invoiceNo?.trim()) {
      messageApi.warning("Please enter an Invoice No.");
      return;
    }
    const invDate =
      header.invoiceDate || header.stockRecDate || dayjs().toISOString();
    if (!invDate) {
      messageApi.warning("Please select an Invoice Date.");
      return;
    }

    setSaving(true);

    try {
      const tenantName = localStorage.getItem("tenantName");
      const isOutstation =
        (header.station || header.supplierObj?.Station || "").toUpperCase() ===
        "OUTSTATION";

      const getRowVal = (row, keys) => {
        if (!row) return "";
        for (const k of keys) {
          if (row[k] !== undefined && row[k] !== null && row[k] !== "") {
            return row[k];
          }
        }
        const rowKeys = Object.keys(row);
        for (const k of keys) {
          const foundKey = rowKeys.find(
            (rk) =>
              rk.toLowerCase().replace(/[\s_]/g, "") ===
              k.toLowerCase().replace(/[\s_]/g, ""),
          );
          if (
            foundKey &&
            row[foundKey] !== undefined &&
            row[foundKey] !== null &&
            row[foundKey] !== ""
          ) {
            return row[foundKey];
          }
        }
        return "";
      };

      const getNum = (row, keys) => {
        const val = getRowVal(row, keys);
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      };

      const getStr = (row, keys) => {
        return String(getRowVal(row, keys) || "").trim();
      };

      let totQty = 0;
      let totGrossAmt = 0;
      let totNetAmt = 0;
      let totGstAmt = 0;
      let totCgstAmt = 0;
      let totSgstAmt = 0;
      let totIgstAmt = 0;

      const purchInvoiceData = [];
      const purchInvoiceDataDet = [];

      dataSource.forEach((row, index) => {
        const sno = index + 1;
        const brandName = getStr(row, ["BRAND", "Brand", "brand", "company"]);
        const itemGroup = getStr(row, [
          "item group",
          "itemgroup",
          "ITEMGROUP",
          "Group Name",
          "groupName",
          "group",
        ]);
        const itemName = getStr(row, [
          "item name",
          "itemname",
          "ITEMNAME",
          "Item Name",
          "itemName",
          "name",
        ]);
        const prodSize = getStr(row, [
          "size",
          "SIZE",
          "sizeinfo",
          "SIZEINFO",
          "Size Info",
          "prodSize",
          "prod size",
        ]);
        const eanNo = getStr(row, [
          "enno",
          "ENNO",
          "eanno",
          "EANNO",
          "itemcode",
          "Item Code",
        ]);
        const styleCode = getStr(row, [
          "style",
          "STYLE",
          "fitting",
          "FITTING",
          "code",
        ]);
        const color = getStr(row, [
          "color",
          "COLOR",
          "Color",
          "colour",
          "COLOUR",
        ]);
        const hsn = getStr(row, [
          "hsncode",
          "HSNCODE",
          "hsn",
          "HSN",
          "HsnCode",
        ]);

        const qty = getNum(row, ["qty", "QTY", "Qty", "quantity", "QUANTITY"]);
        const mrp = getNum(row, [
          "custprice",
          "CUSTPRICE",
          "mrp",
          "MRP",
          "CustPrice",
        ]);
        const unitRate = getNum(row, [
          "dlrprice",
          "DLRPRICE",
          "DLRprice",
          "unitrate",
          "UNITRATE",
        ]);
        const taxPerc = getNum(row, [
          "gst",
          "GST",
          "taxperc",
          "TAXPERC",
          "gstperc",
        ]);
        const basicVal = unitRate * qty;
        const taxAmt =
          getNum(row, ["taxamt", "TAXAMT", "gstamt"]) ||
          basicVal * (taxPerc / 100);
        const totalVal = basicVal + taxAmt;
        const grosDPrice = (unitRate * taxPerc) / 100 + unitRate;
        const vatAmt = (basicVal * taxPerc) / 100;
        const gamountVal = vatAmt + basicVal;
        const entryNoVal = header.entryNo || 0;
        const ecodeVal = `${entryNoVal} / ${sno}`;

        let cgstRate = 0,
          cgstAmt = 0;
        let sgstRate = 0,
          sgstAmt = 0;
        let igstRate = 0,
          igstAmt = 0;

        if (isOutstation) {
          igstRate = taxPerc;
          igstAmt = taxAmt;
        } else {
          cgstRate = taxPerc / 2;
          cgstAmt = taxAmt / 2;
          sgstRate = taxPerc / 2;
          sgstAmt = taxAmt / 2;
        }

        totQty += qty;
        totGrossAmt += basicVal;
        totNetAmt += totalVal;
        totGstAmt += taxAmt;
        totCgstAmt += cgstAmt;
        totSgstAmt += sgstAmt;
        totIgstAmt += igstAmt;

        const detailObj = {
          invno: header.invoiceNo,
          invdate: dayjs(header.invoiceDate || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
          area: header.supplierObj?.area || header.city || "",
          custName:
            header.supplierObj?.Custname ||
            header.supplierObj?.Dealername ||
            "",
          sno: sno,
          company: brandName,
          groupName: itemGroup,
          itemName: itemName,
          prodSize: prodSize,
          itemcode: eanNo,
          designCode: row._dcode || styleCode || "",
          code: styleCode,
          qty: qty,
          dlrPrice: unitRate,
          custPer: 0,
          custPrice: mrp,
          disper: 0,
          disAmt: 0,
          amount: basicVal,
          dlrDISPER: 0,
          dlrDISAMT: 0,
          token: 0,
          vatamt: vatAmt,
          gamount: gamountVal,
          netamt: totalVal,
          cancelled: "NO",
          sStatus: false,
          sstatus: false,
          hid: 0,
          HID: 0,
          bno: 0,
          colour: color,
          fitting: styleCode,
          sleves: "-",
          sleeves: "-",
          custVPER: 0,
          ecode: ecodeVal,
          ECODE: ecodeVal,
          eqty: qty,
          entryno: entryNoVal,
          entrydate: dayjs(header.date || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
          acity: header.city || header.supplierObj?.city || "",
          agentname: "",
          offer: 0,
          grosDPrice: grosDPrice,
          grossdprice: grosDPrice,
          taans: false,
          trans: false,
          fsize: "",
          tsize: "",
          differ: "",
          iteM_QTY: qty,
          dlrPLUSPER: 0,
          dlrPLUSAMT: 0,
          pricetype: "",
          cpless: 0,
          cpplus: 0,
          branchname: "",
          splititem: false,
          ctoken: 0,
          pvatper: 0,
          pvatamt: 0,
          cno: 0,
          ptype: header.purchaseType?.PType || "",
          icodetype: "0",
          gst: taxPerc,
          cgstrate: cgstRate,
          cgstamt: cgstAmt,
          sgstrate: sgstRate,
          sgstamt: sgstAmt,
          igstrate: igstRate,
          igstamt: igstAmt,
          hsncode: hsn,
          gstamt: taxAmt,
          resellerprice: 0,
          semiwsalerprice: 0,
          floor: "",
          free: 0,
          totpcs: qty,
          dlrdamt: 0,
          boxes: 0,
          loosepcs: 0,
          packpcs: 0,
          caseratedp: 0,
          caseratecp: 0,
          caseraterp: 0,
          actqty: qty,
          dlrdispeR2: 0,
          dlrdisamT2: 0,
          dlrdispeR3: 0,
          dlrdisamT3: 0,
          dplusper: 0,
          dplusamt: 0,
          length: 0,
          lqty: 0,
          wper: 0,
          wprice: 0,
          oqtY2: 0,
          oqtY3: 0,
          oqtY4: 0,
          pntmodel: "",
          smodel: "",
          fyear: financialYearFull(),
          clouD_UPLOAD: true,
        };

        purchInvoiceData.push(detailObj);

        purchInvoiceDataDet.push({
          ...detailObj,
          srecdate: dayjs(header.stockRecDate || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
          terminalid: Number(designBase.prefix) || 0,
          maxnum: (designBase.maxSeq || 0) + 1 + index,
        });
      });

      const masterObj = {
        invno: header.invoiceNo,
        invdate: dayjs(header.invoiceDate || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
        pType: header.purchaseType?.PType || "",
        area: header.supplierObj?.area || header.city || "",
        partyname:
          header.supplierObj?.Custname || header.supplierObj?.Dealername || "",
        grossamt: totGrossAmt,
        disrate: 0,
        tradedis: 0,
        adjPer: 0,
        adjAmt: 0,
        vatrate: 0,
        vatamt: 0,
        evat: 0,
        evatamt: 0,
        netamt: totNetAmt,
        paidAmt: 0,
        balanceAmt: totNetAmt,
        cancelled: "NO",
        narration: "",
        lrno: "",
        lrdate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
        transport: "",
        ddays: 0,
        weight: "",
        srecdate: dayjs(header.stockRecDate || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
        fcharges: 0,
        hid: 0,
        mode: "",
        accno: "",
        cardno: "",
        amount: 0,
        ddchr: 0,
        cstatus: true,
        custPer: 0,
        charges: 0,
        pstatus: "",
        exduty: true,
        totalamt: totGrossAmt,
        discount: 0,
        cvper: 0,
        cvamt: 0,
        ocharges: 0,
        station: header.station || header.supplierObj?.Station || "LOCAL",
        oaddition: 0,
        entryno: header.entryNo || 0,
        entrydate: dayjs(header.date || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
        acity: header.city || header.supplierObj?.city || "",
        agentname: "",
        taans: true,
        branchname: "",
        dlrplusamt: 0,
        crNO: "",
        crAMT: 0,
        crStatus: true,
        imgpatH1: "",
        imgpatH2: "",
        imgpatH3: "",
        imgpatH4: "",
        imgpatH5: "",
        invpayableamt: totNetAmt,
        invpaidamt: 0,
        invrtnamt: 0,
        invbalamt: totNetAmt,
        invstatus: true,
        totcgstrate: isOutstation
          ? 0
          : dataSource.length
            ? totGstAmt / 2 / dataSource.length
            : 0,
        totcgstamt: totCgstAmt,
        totsgstrate: isOutstation
          ? 0
          : dataSource.length
            ? totGstAmt / 2 / dataSource.length
            : 0,
        totsgstamt: totSgstAmt,
        totigstrate: isOutstation
          ? dataSource.length
            ? totGstAmt / dataSource.length
            : 0
          : 0,
        totigstamt: totIgstAmt,
        totgstamt: totGstAmt,
        floor: "",
        smodel: "",
        totqty: totQty,
        fyear: financialYearFull(),
        clouD_UPLOAD: true,
      };

      const payload = {
        purchInvoiceMast: [masterObj],
        purchInvoiceData: purchInvoiceData,
        purchInvoiceDataDet: purchInvoiceDataDet,
      };

      const response = await axios.post(
        `${CREATE_jwel}/api/PurchInvoice/Save`,
        payload,
        {
          headers: { tenantName },
        },
      );

      if (response.status === 200 || response.status === 201) {
        const partyName =
          header.supplierObj?.Custname || header.supplierObj?.Dealername || "";
        const areaName = header.supplierObj?.area || header.city || "";
        const pTypeStr = header.purchaseType?.PType || "";

        const currentStockPayload = [
          {
            area: areaName,
            partyname: partyName,
            billno: header.invoiceNo || "",
            billdate: dayjs(header.invoiceDate || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
            entryno: header.entryNo || 0,
            entrydate: dayjs(header.date || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
            particulars: pTypeStr || "PURCHASE",
            jamaqty: totQty,
            namaqty: 0,
            jama: totNetAmt,
            nama: 0,
            narration: "",
            hid: 0,
            agentcity: "",
            agentname: "",
            tranS_TYPE: pTypeStr,
            cusT_TYPE: "",
            sno: 1,
            pstatus: "",
            salestype: "",
            chequeno: "",
            chqdate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
            chqamt: 0,
            chqstatus: true,
            accno: "",
            chqcancell: "",
            description: "",
            lname: partyName,
            groupname: "",
            cjama: 0,
            cnama: 0,
            pjama: 0,
            pnama: 0,
            stype: "",
            sdate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
            gjama: 0,
            gnama: 0,
            touch: 0,
            vno: "",
            vtype: "",
            cuT_METAL: 0,
            cuT_TOUCH: 0,
            cuT_FINE: 0,
            rate: 0,
            cuT_AMOUNT: 0,
            vaT_PER: 0,
            vaT_AMOUNT: 0,
            cuT_NETAMOUNT: 0,
            jstonewt: 0,
            jnwt: 0,
            jamount: 0,
            nstonewt: 0,
            nnwt: 0,
            namount: 0,
            clouD_UPLOAD: true,
          },
        ];

        try {
          await axios.post(
            `${CREATE_jwel}/api/CurrentStockApi/Save`,
            currentStockPayload,
            {
              headers: { tenantName },
            },
          );
        } catch (stockErr) {
          console.error("CurrentStockApi/Save error:", stockErr);
        }

        messageApi.success(
          `${tableData.length} record(s) saved successfully for "${header.purchaseType.PType}" — ${header.supplierObj.Dealername || header.supplierObj.Custname}`,
        );
        handleReset();
      } else {
        messageApi.error(
          response.data?.message || "Failed to save purchase invoice.",
        );
      }
    } catch (err) {
      console.error("Save error:", err);
      messageApi.error(
        err.response?.data?.message ||
          err.message ||
          "Error saving purchase invoice.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── reset screen to starting stage ── */
  const handleReset = () => {
    setHeaders([]);
    setTableData([]);
    setFileName("");
    setHeader((prev) => ({
      entryNo: "",
      date: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
      purchaseType: prev.purchaseType || null,
      city: "",
      supplierObj: null,
      shortName: "",
      address: "",
      gstin: "",
      panNo: "",
      station: "LOCAL",
      invoiceNo: "",
      invoiceDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
      stockRecDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    loadDesignBase();
    setFocusTrigger((prev) => prev + 1);
  };

  /* ── columns ── */
  const SNO_W = 66;
  const DATA_W = 150;
  const DCODE_W = 150;

  const buildColumns = () => {
    if (!headers.length) return [];

    const totalW = SNO_W + DCODE_W + headers.length * DATA_W;

    const hdrCell =
      (leftPx, widthPx, extraClass = "") =>
      () => ({
        className: extraClass,
        style: {
          backgroundColor: hdrColor((leftPx + widthPx / 2) / totalW),
        },
      });

    const snoCol = {
      title: "S.No",
      dataIndex: "_sno",
      key: "_sno",
      width: SNO_W,
      align: "center",
      fixed: "left",
      onHeaderCell: hdrCell(0, SNO_W, "eup-sno-th"),
      onCell: () => ({ className: "eup-sno-td" }),
      render: (v) => <SnoBadge n={v} />,
    };
    const dcodeCol = {
      title: "Design Code",
      dataIndex: "_dcode",
      key: "_dcode",
      width: DCODE_W,
      fixed: "left",
      onHeaderCell: hdrCell(SNO_W, DCODE_W, "eup-sno-th"),
      onCell: () => ({ className: "eup-sno-td" }),
      render: (v) => <strong style={{ color: C.pri }}>{v || "—"}</strong>,
    };

    const dataCols = headers.map((h, idx) => ({
      title: h,
      dataIndex: h,
      key: h,
      ellipsis: true,
      width: DATA_W,
      onHeaderCell: hdrCell(SNO_W + DCODE_W + idx * DATA_W, DATA_W),
      render: (val) => (
        <span style={{ fontSize: 13, color: C.textPri }}>{val || "—"}</span>
      ),
    }));

    return [snoCol, dcodeCol, ...dataCols];
  };

  return (
    <div className="eup-wrap">
      <style>{GLOBAL_CSS}</style>
      {ctx}

      <div className="eup-header">
        <div className="eup-header-shimmer" />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            position: "relative",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              color: "#fff",
            }}
          >
            <TableOutlined />
          </div>
          <div>
            <div className="eup-header-title">Excel Upload Purchase</div>
            <div className="eup-header-sub">Purchase · Excel Import</div>
          </div>
        </div>

        {hasData && (
          <div style={{ display: "flex", gap: 10, position: "relative" }}>
            <div className="eup-chip">
              <span className="eup-chip-val">{tableData.length}</span>
              <span className="eup-chip-lbl">Records</span>
            </div>
            <div className="eup-chip">
              <span className="eup-chip-val">{headers.length}</span>
              <span className="eup-chip-lbl">Columns</span>
            </div>
          </div>
        )}
      </div>

      <PurchaseHeaderForm
        value={header}
        onChange={setHeader}
        disabled={saving}
        onFinish={() => uploadBtnRef.current?.focus()}
        focusTrigger={focusTrigger}
      />

      <div className="eup-toolbar">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flex: 1,
            flexWrap: "wrap",
          }}
        >
          {hasData ? (
            <span className="eup-file-pill">
              <FileExcelOutlined style={{ fontSize: 14 }} />
              {fileName}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: C.textMuted }}>
              No file loaded — fill in the header above, then upload your Excel
            </span>
          )}

          <button
            ref={uploadBtnRef}
            className="eup-btn eup-btn-upload"
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadOutlined />
            {hasData ? "Upload New File" : "Upload Excel"}
          </button>

          {hasData && (
            <button className="eup-btn eup-btn-clear" onClick={handleReset}>
              <CloseOutlined />
              Clear
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={onInputChange}
        />

        {hasData && (
          <button
            className="eup-btn eup-btn-save"
            onClick={handleSaveAll}
            disabled={saving}
          >
            <SaveOutlined />
            {saving ? "Saving…" : `Quick Save (${tableData.length})`}
          </button>
        )}
      </div>

      {!hasData && !loading && (
        <DropZone
          onFile={() => fileInputRef.current?.click()}
          dragging={dragging}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragLeave={onDragLeave}
        />
      )}

      {loading && <Loader />}

      {hasData && (
        <div className="eup-card">
          <div className="eup-subhdr">
            <span className="eup-subhdr-text">
              Showing&nbsp;
              <strong>{tableData.length}</strong>&nbsp;record(s) across&nbsp;
              <strong>{headers.length}</strong>&nbsp;column(s)
            </span>
          </div>

          {/* scrollable table */}
          <div className="eup-table-wrap" ref={tableWrapRef}>
            <Table
              className="eup-table"
              columns={buildColumns()}
              dataSource={dataSource}
              rowKey="key"
              pagination={false}
              scroll={{
                x: SNO_W + DCODE_W + headers.length * DATA_W,
                y: Math.max(tableBodyH - HEADER_H, 100),
              }}
              size="small"
              bordered={false}
            />
          </div>

          {/* footer save bar */}
          <div className="eup-footer">
            <div className="eup-footer-info">
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: C.ok,
                  display: "inline-block",
                  boxShadow: `0 0 0 3px ${C.okLight}`,
                }}
              />
              <strong>{tableData.length}</strong>&nbsp;record(s) ready to save
              {header.purchaseType && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    background: `linear-gradient(135deg, #312e81, ${C.pri})`,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 12,
                    padding: "2px 10px",
                    letterSpacing: 0.3,
                  }}
                >
                  {header.purchaseType.PType}
                  <span style={{ opacity: 0.65, fontSize: 10 }}>
                    [{header.purchaseType.PCode}]
                  </span>
                </span>
              )}
              {header.supplierObj && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: "#ecfdf5",
                    border: "1px solid #6ee7b7",
                    color: "#065f46",
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 12,
                    padding: "2px 10px",
                  }}
                >
                  {header.supplierObj.Dealername}
                  {header.city && (
                    <span style={{ opacity: 0.65 }}>· {header.city}</span>
                  )}
                </span>
              )}
              {header.invoiceNo && (
                <span
                  style={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}
                >
                  Inv&nbsp;
                  <strong style={{ color: C.textPri }}>
                    {header.invoiceNo}
                  </strong>
                </span>
              )}
            </div>

            <button
              className="eup-save-final"
              onClick={handleSaveAll}
              disabled={saving}
            >
              <SaveOutlined style={{ fontSize: 16 }} />
              {saving ? "Saving…" : "Save Purchase Data"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelUploadPurchase;
